"use client";

import { useMemo } from "react";
import { Plus, X } from "lucide-react";
import { SortableList } from "./SortableList";
import { AiAssistButton } from "./AiAssistButton";

export interface RubricCriterion {
  criterion: string;
  weight: number;
}

const QUICK_CRITERIA = [
  "Accuracy",
  "Grammar",
  "Vocabulary",
  "Fluency",
  "Task completion",
  "Coherence",
] as const;

/**
 * Shared rubric editor reused by `FreeAnswerBuilder`, `FreeWritingBuilder`,
 * and the legacy `RubricEditor` inside ExercisesPage.
 *
 * Features:
 *   - Sortable list of criteria (drag to reorder)
 *   - Quick-add chips for common criteria
 *   - Live total-weight indicator (green at 100, amber otherwise)
 *   - Optional Gemini assist that drafts a rubric from the prompt + level
 *   - Inline validation: weights must sum to 100
 */
export interface RubricEditorProps {
  value: RubricCriterion[];
  onChange: (next: RubricCriterion[]) => void;
  /** Optional prompt text to feed the AI rubric assist. */
  promptHint?: string;
  /** CEFR level for AI assist. */
  level?: "A1" | "A2" | "B1" | "B2" | "C1";
}

export function RubricEditor({ value, onChange, promptHint, level }: RubricEditorProps) {
  const total = useMemo(() => value.reduce((s, r) => s + r.weight, 0), [value]);

  const quickAdd = (name: string) => {
    if (value.some((v) => v.criterion.toLowerCase() === name.toLowerCase())) return;
    const defaultWeight = value.length === 0 ? 100 : 20;
    onChange([...value, { criterion: name, weight: defaultWeight }]);
  };

  return (
    <div className="rounded-md border border-[#FF9F0A]/30 bg-[#FF9F0A]/10 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-[#FF9F0A]">
          Gemini rubric {value.length === 0 && "(defaults applied at save)"}
        </p>
        <p
          className={
            "text-xs font-mono " +
            (total === 100 ? "text-[#34C759]" : "text-[#FF9F0A]")
          }
        >
          Total weight: {total}
        </p>
      </div>

      {/* Quick-add chips — one-click seed for common criteria. */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E8E93]">
          Quick add:
        </span>
        {QUICK_CRITERIA.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => quickAdd(n)}
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/30 hover:bg-[#FF9F0A]/25"
          >
            + {n}
          </button>
        ))}
        {promptHint && level && (
          <AiAssistButton
            kind="rubric"
            prompt={promptHint}
            level={level}
            label="Draft rubric"
            onApply={(content) => {
              if (Array.isArray(content)) {
                onChange(content as RubricCriterion[]);
              }
            }}
          />
        )}
      </div>

      <div className="mt-3">
        <SortableList
          items={value}
          getKey={(_, i) => `rubric-${i}`}
          onReorder={onChange}
          emptyState={
            <div className="text-center py-4">
              <p className="text-[11px] font-mono text-[#8E8E93]">
                No criteria yet — add one above or use ✨ Draft rubric
              </p>
            </div>
          }
          renderRow={({ item: row, index: i, dragHandle }) => (
            <div className="flex items-center gap-2 w-full">
              {dragHandle}
              <input
                value={row.criterion}
                onChange={(e) =>
                  onChange(
                    value.map((r, j) => (j === i ? { ...r, criterion: e.target.value } : r)),
                  )
                }
                placeholder="Criterion (e.g. Grammar)"
                className="h-9 flex-1 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#141414] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
                aria-label={`Criterion ${i + 1} name`}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={String(row.weight)}
                  onChange={(e) =>
                    onChange(
                      value.map((r, j) =>
                        j === i ? { ...r, weight: Number(e.target.value) || 0 } : r,
                      ),
                    )
                  }
                  className="h-9 w-16 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#141414] px-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A] text-right"
                  aria-label={`Criterion ${i + 1} weight`}
                />
                <span className="text-[10px] font-mono text-[#8E8E93]">%</span>
              </div>
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="rounded-md p-1.5 text-[#FF9F0A] hover:bg-[#FF9F0A]/15"
                aria-label={`Remove criterion ${i + 1}`}
              >
                <X size={14} />
              </button>
            </div>
          )}
        />
      </div>

      <button
        type="button"
        onClick={() => onChange([...value, { criterion: "", weight: 0 }])}
        className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#1C1C1E] border border-dashed border-[rgba(255,255,255,0.12)] text-xs text-[#8E8E93] hover:text-[#FAFAFA] hover:border-[#FFD60A]/40 w-full justify-center"
      >
        <Plus size={14} />
        Add criterion
      </button>
    </div>
  );
}

/**
 * Default rubric presets used when the AI assist endpoint isn't configured
 * or the admin clicks "Reset to defaults".
 */
export const DEFAULT_RUBRICS: Record<string, RubricCriterion[]> = {
  free_answer: [
    { criterion: "Accuracy", weight: 60 },
    { criterion: "Grammar", weight: 40 },
  ],
  free_writing: [
    { criterion: "Grammar", weight: 30 },
    { criterion: "Vocabulary", weight: 30 },
    { criterion: "Task completion", weight: 40 },
  ],
};

export const buildDefaultRubricFor = (kind: "free_answer" | "free_writing"): RubricCriterion[] =>
  DEFAULT_RUBRICS[kind].map((c) => ({ ...c }));